import React from 'react';
import PropTypes from 'prop-types';
import {dispatch, connect} from 'utils/state_manager';

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dropdown,
  DropdownMenuItem,
  Stack,
  TextField,
  Typography,
} from 'views/components';

import {Translate, translate, CaregiverAutocomplete, MarkdownEditor} from 'views/module';
import simplifyQuestionss from 'utils/user_bio/simplify_questions';
import backend from 'utils/backend';
import moment from 'moment-timezone';

const AddCaregiver = (props) => {
  const [caregiver, setCaregiver] = React.useState(null);
  const [caregiverStatus, setCaregiverStatus] = React.useState('REVIEW');
  const [customRecommendation, setCustomRecommendation] = React.useState('');
  const [recommendationChips, setRecommendationChips] = React.useState([]);
  const [availabilityText, setAvailabilityText] = React.useState('');
  const [availabilityOverlap, setAvailabilityOverlap] = React.useState({});
  const [availabilityLastUpdatedTime, setAvailabilityLastUpdatedTime] = React.useState(null);
  const [newChipInput, setNewChipInput] = React.useState('');
  const [generatingRecommendation, setGeneratingRecommendation] = React.useState(false);

  const {
    onClose,
    addCaregiver,
    clientRn,
    statusProgressWrap: progressWrap,
  } = props;

  const handleSetCaregiver = React.useCallback((selectedCaregiver) => {
    setCaregiver(selectedCaregiver);
  }, [caregiver]);

  const requiredBioFields = React.useMemo(() => ([
    'firstName',
    'lastName',
    'city',
    'province',
    'shortBio',
    'personalBio',
    'caregiverMotivation',
  ]), []);

  const caregiverQuestions = React.useMemo(() => {
    const caregiverBioQuestions = caregiver?.bio?.questions || {};
    return simplifyQuestionss(caregiverBioQuestions);
  }, [caregiver]);

  const missingFields = React.useMemo(() => {
    return requiredBioFields.filter(
        (field) =>
          !caregiverQuestions?.[field] ||
        (typeof caregiverQuestions[field] === 'string' && !caregiverQuestions[field].trim()),
    );
  }, [caregiverQuestions, requiredBioFields]);

  const missingFieldsSentence = React.useMemo(() => {
    return missingFields
        .map((field) =>
          translate(`caregiver.CaregiverDetail.Settings.ProfileUrl.RequiredFields.${field}`),
        )
        .join(', ');
  }, [missingFields]);

  const showMissingFieldsWarning = Boolean(caregiver?.caregiverRn && missingFields.length > 0);

  const availabilityDays = Object.keys(availabilityOverlap || {});
  const hasAvailabilityText = Boolean(availabilityText?.trim());
  const hasAvailabilityOverlap = availabilityDays.some((day) => (
    availabilityOverlap?.[day]?.start || availabilityOverlap?.[day]?.end
  ));

  const formatAvailabilityOverlapValue = (value) => {
    if (!value) {
      return '-';
    }

    return moment(value).tz('America/Toronto').format('h:mm a, MMMM Do YYYY');
  };

  const getAvailabilityOverlapDisplay = (dayOverlap) => {
    const start = formatAvailabilityOverlapValue(dayOverlap?.start);
    const end = formatAvailabilityOverlapValue(dayOverlap?.end);

    if (start === '-' && end === '-') {
      return '-';
    }

    return `${start} - ${end}`;
  };

  const handleAddCaregiver = () => {
    const caregiverData = {
      caregiverRn: caregiver.caregiverRn,
      status: caregiverStatus,
      availabilityOverlap,
      availability: availabilityText || null,
      availabilityLastUpdatedTime: availabilityLastUpdatedTime || new Date().toISOString(),
    };

    if (customRecommendation.trim()) {
      caregiverData.recommendation = customRecommendation.trim();
      caregiverData.recommendationChips = recommendationChips;
    }
    addCaregiver(caregiverData);
  };

  const handleGenerateReview = React.useCallback(() => {
    const caregiverRnForReview = caregiver?.caregiverRn;

    setGeneratingRecommendation(true);
    progressWrap(async () => {
      try {
        const result = await backend.clientSupport.generateCaregiverRecommendation(clientRn, caregiverRnForReview);
        if (!result) {
          return;
        }

        const recommendation = result?.recommendation;
        const recommendationText = recommendation
            .map((line) => line.trim())
            .filter((line) => line.length > 0)
            .map((line) => (line.startsWith('- ') ? line : `- ${line}`))
            .join('\n');
        setCustomRecommendation(recommendationText);

        const recommendationChips = result?.recommendationChips;
        setRecommendationChips(recommendationChips);
        const availability = typeof result?.availability === 'string' ? result.availability.trim() : '';
        const availabilityOverlap = result?.availabilityOverlap;
        const availabilityLastUpdatedTime = (
          typeof result?.availabilityLastUpdatedTime === 'string' &&
          result.availabilityLastUpdatedTime
        ) ? result.availabilityLastUpdatedTime : new Date().toISOString();
        setAvailabilityText(availability);
        setAvailabilityOverlap(availabilityOverlap);
        setAvailabilityLastUpdatedTime(availabilityLastUpdatedTime);
      } catch (error) {
        console.error('Client-caregiver recommendation request failed', error);
        throw error;
      } finally {
        setGeneratingRecommendation(false);
      }
    }, {debugString: 'CLIENT_GENERATE_CAREGIVER_RECOMMENDATION'});
  }, [caregiver, clientRn, progressWrap]);

  const handleAddChip = React.useCallback(() => {
    const chip = newChipInput.trim();
    if (!chip) {
      return;
    }

    setRecommendationChips((existingChips) => {
      if (existingChips.includes(chip)) {
        return existingChips;
      }
      return [...existingChips, chip];
    });
    setNewChipInput('');
  }, [newChipInput]);

  const handleRemoveChip = React.useCallback((chipIndex) => {
    setRecommendationChips((existingChips) => {
      return existingChips.filter((chip, index) => index !== chipIndex);
    });
  }, []);

  const CAREGIVER_STATUS = {
    Review: 'REVIEW',
    Active: 'ACTIVE',
  };

  const allStatus = translate('client.ClientDetail.CaregiverConnections.AddCaregiver.Status');

  return (
    <Dialog onClose={onClose} open={true}>
      <DialogTitle onClose={onClose}>
        <Translate expression='client.ClientDetail.CaregiverConnections.AddCaregiver.Title' />
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{my: 1}}>
          <CaregiverAutocomplete
            label={translate('client.ClientDetail.CaregiverConnections.AddCaregiver.CaregiverSearch')}
            selectedCaregiver={caregiver}
            setSelectedCaregiver={handleSetCaregiver}
          />
          {
            showMissingFieldsWarning &&
            <Alert severity="warning">
              <Translate
                expression="client.ClientDetail.CaregiverConnections.AddCaregiver.IncompleteProfile"
                substitute={{missingFields: missingFieldsSentence}}
              />
            </Alert>
          }
          <Dropdown
            onChange={setCaregiverStatus}
            value={caregiverStatus}
          >
            {
              Object.entries(allStatus).map(([k, v]) => (
                <DropdownMenuItem key={k} value={CAREGIVER_STATUS[k]}>
                  {v}
                </DropdownMenuItem>
              ))
            }
          </Dropdown>
          <Typography variant="subtitle2">
            Recommendation
          </Typography>
          <MarkdownEditor
            value={customRecommendation}
            onChange={setCustomRecommendation}
            autoFocus={false}
          />
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              label="Add chip"
              value={newChipInput}
              onChange={setNewChipInput}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleAddChip();
                }
              }}
            />
            <Button
              variant="outlined"
              onClick={handleAddChip}
              disabled={!newChipInput.trim()}
            >
              +
            </Button>
          </Stack>
          {
            recommendationChips.length > 0 &&
            <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
              {
                recommendationChips.map((chip, index) => (
                  <Chip
                    key={`${chip}-${index}`}
                    label={chip}
                    onDelete={() => handleRemoveChip(index)}
                  />
                ))
              }
            </Box>
          }
          {
            hasAvailabilityText &&
            <Stack spacing={0.5}>
              <Typography variant="subtitle2">
                Availability
              </Typography>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1.5,
                  minHeight: 56,
                  backgroundColor: 'background.paper',
                }}
              >
                <Typography variant="body2">
                  {availabilityText}
                </Typography>
              </Box>
            </Stack>
          }
          {
            hasAvailabilityOverlap &&
            <Stack spacing={0.5}>
              <Typography variant="subtitle2" sx={{mt: 1}}>
                Availability overlap
              </Typography>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1.5,
                  backgroundColor: 'background.paper',
                }}
              >
                <Stack spacing={0.5}>
                  {
                    availabilityDays.map((day) => (
                      <Stack key={day} direction="row" spacing={1}>
                        <Typography variant="body2" sx={{minWidth: 86, fontWeight: 500}}>
                          {day}
                        </Typography>
                        <Typography variant="body2" sx={{wordBreak: 'break-word'}}>
                          {getAvailabilityOverlapDisplay(availabilityOverlap?.[day])}
                        </Typography>
                      </Stack>
                    ))
                  }
                </Stack>
              </Box>
            </Stack>
          }
        </Stack>
      </DialogContent>
      <DialogActions sx={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
        <Button
          onClick={handleGenerateReview}
          disabled={!caregiver?.caregiverRn || generatingRecommendation}
          loading={generatingRecommendation}
        >
          Generate review
        </Button>
        <Stack direction="row" spacing={1}>
          <Button variant='outlined' onClick={onClose}>
            <Translate expression='generic.Close' />
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleAddCaregiver}
          >
            <Translate expression='client.ClientDetail.CaregiverConnections.AddCaregiver.AddCaregiver' />
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

AddCaregiver.propTypes = {
  onClose: PropTypes.func.isRequired,
  addCaregiver: PropTypes.func.isRequired,
  clientRn: PropTypes.string.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(AddCaregiver));
